import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "../../interfaces/DecisionPriority";
import { Board, Move, IPiece, Position, EffectType } from "../../../../types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  clonePieceWithPrototype,
  applyMoveWithSnakeRespawn,
} from "../../../utils/MCTSUtils";
import { MCTSMoveGenerationService } from "../../../utils/MCTSMoveGenerationService";

/**
 * Priority 3: Race Block Description
 * @LLM for clarity this means MOVING a piece that CANNOT be captured by the enemy king in to a winning race position! This does not mean moving a piece that CAN be captured by the enemy king
 * in to a winning race position! That does not block anything!
 * This does not include capturing the enemy king or disrupting a movement pattern like Monkey Leap or Goat traverse, those are handled by other priorities!
 * This priority should simply - check all enemy king moves for a single ply and see if they can complete a race victory! Then check if we have a move that can block all possible outcomes!
 * If our move can stop all possible race victories of the enemy king, it must be taken! (Early exit - forced move)
 */

export class RaceBlockPriority extends BaseDecisionPriority {
  readonly priority = 3;
  readonly name = "Race Block";
  readonly description = "CRITICAL: Block enemy king 1-move race victories";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    // Always relevant - we must check for enemy king race threats every turn
    return true;
  }

  analyze(context: DecisionContext): DecisionResult {
    // Reset logs for this analysis
    this.analysisLogs = [];

    const gameState = context.gameState;
    const isPlayerTurn = gameState.isLightTurn;
    const enemyColor = isPlayerTurn ? "black" : "white";

    // Find enemy king and check if it can win on the next turn
    const kingRaceThreats = this.findEnemyKingRaceThreats(
      gameState,
      enemyColor
    );

    if (kingRaceThreats.length === 0) {
      return {
        shouldEarlyExit: false,
        reasoning: `${this.name}: No enemy king race threats`,
        priority: this.priority,
        diagnostics: {
          logs: this.analysisLogs,
        },
      };
    }

    this.addLog(`🚨 ENEMY KING CAN WIN IN 1 MOVE!`);
    this.addLog(
      `King can reach: [${kingRaceThreats
        .map((pos) => `${pos.row},${pos.col}`)
        .join(", ")}]`
    );

    // Check for both active and defensive blocking moves
    const activeBlockingMoves = this.findActiveBlockingMoves(
      context.allPossibleMoves,
      kingRaceThreats,
      gameState
    );

    const defensiveBlockingMoves = this.findDefensiveBlockingMoves(
      context.allPossibleMoves,
      gameState,
      enemyColor
    );

    const allBlockingMoves = [
      ...activeBlockingMoves,
      ...defensiveBlockingMoves,
    ];

    if (allBlockingMoves.length === 0) {
      this.addLog(`💀 NO BLOCKING MOVES - POSITION IS LOST!`);
      return {
        shouldEarlyExit: false,
        reasoning: `${this.name}: CRITICAL: No moves can block enemy king race - position lost!`,
        priority: this.priority,
        diagnostics: {
          logs: this.analysisLogs,
        },
      };
    }

    // EARLY EXIT - This is a forced move situation
    this.addLog(
      `🛡️ FORCED BLOCK: ${allBlockingMoves.length} moves can block king race`
    );
    this.addLog(`  - Active blocks: ${activeBlockingMoves.length}`);
    this.addLog(`  - Defensive blocks: ${defensiveBlockingMoves.length}`);

    // FIXED: Always early exit with a selected move for forced blocking situations
    // Prioritize active blocks over defensive blocks
    const bestBlockingMove =
      activeBlockingMoves.length > 0
        ? activeBlockingMoves[0]
        : allBlockingMoves[0];

    this.addLog(
      `🎯 Selected blocking move: [${bestBlockingMove.fromPosition.row},${bestBlockingMove.fromPosition.col}] → [${bestBlockingMove.move.position.row},${bestBlockingMove.move.position.col}]`
    );
    this.addLog(
      `   Type: ${
        activeBlockingMoves.includes(bestBlockingMove)
          ? "Active block"
          : "Defensive block"
      }`
    );

    return this.createEarlyExitResult(
      bestBlockingMove,
      `${this.name}: FORCED - Must block enemy king's immediate race victory`,
      {
        forcedBlock: true,
        kingThreats: kingRaceThreats.length,
        activeBlocks: activeBlockingMoves.length,
        defensiveBlocks: defensiveBlockingMoves.length,
        totalBlocking: allBlockingMoves.length,
        logs: this.analysisLogs,
      }
    );
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  private findEnemyKingRaceThreats(
    gameState: any,
    enemyColor: "white" | "black"
  ): Position[] {
    const victoryRow = enemyColor === "white" ? 7 : 0;
    const kingThreats: Position[] = [];

    // Find the enemy king
    const enemyKing = this.findKing(gameState.board, enemyColor);
    if (!enemyKing) {
      this.addLog(`⚠️ No ${enemyColor} king found`);
      return [];
    }

    this.addLog(
      `👑 Found ${enemyColor} king at [${enemyKing.row},${enemyKing.col}]`
    );

    // Use move generation service to get king's actual moves
    const moveService = MCTSMoveGenerationService.getInstance();

    // Temporarily flip turn to generate enemy king moves
    const originalTurn = gameState.isLightTurn;
    gameState.isLightTurn = enemyColor === "white";

    try {
      // Generate all possible moves for enemy
      const allEnemyMoves = moveService.getAllMoves(gameState);

      // Filter to only king moves
      const kingMoves = allEnemyMoves.filter(
        (moveInfo) =>
          moveInfo.fromPosition.row === enemyKing.row &&
          moveInfo.fromPosition.col === enemyKing.col
      );

      this.addLog(`King has ${kingMoves.length} possible moves`);

      // Check which moves reach the victory row
      for (const moveInfo of kingMoves) {
        if (moveInfo.move.position.row === victoryRow) {
          kingThreats.push(moveInfo.move.position);
        }
      }
    } finally {
      // Restore original turn
      gameState.isLightTurn = originalTurn;
    }

    return kingThreats;
  }

  private findKing(board: any[][], color: "white" | "black"): Position | null {
    const isLight = color === "white";

    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[row].length; col++) {
        const square = board[row][col];
        if (square.Pieces && square.Pieces.length > 0) {
          const piece = square.Pieces[0];
          if (piece.isLight === isLight && piece.isKing) {
            return { row, col };
          }
        }
      }
    }
    return null;
  }

  private findKingBlockingMoves(
    allMoves: MoveInfo[],
    kingThreats: Position[],
    gameState: any
  ): MoveInfo[] {
    // This method is now just for active blocking moves
    return this.findActiveBlockingMoves(allMoves, kingThreats, gameState);
  }

  private findActiveBlockingMoves(
    allMoves: MoveInfo[],
    kingThreats: Position[],
    gameState: any
  ): MoveInfo[] {
    const blockingMoves: MoveInfo[] = [];

    for (const moveInfo of allMoves) {
      // Skip moves that capture the enemy king (handled by other priorities)
      if (this.moveCaptures(moveInfo, gameState, "king")) {
        continue;
      }

      // Check if this move blocks any king threat position
      if (this.doesMoveBlockKingThreats(moveInfo, kingThreats, gameState)) {
        blockingMoves.push(moveInfo);
      }
    }

    return blockingMoves;
  }

  private findDefensiveBlockingMoves(
    allMoves: MoveInfo[],
    gameState: any,
    enemyColor: "white" | "black"
  ): MoveInfo[] {
    const safeDefensiveMoves: MoveInfo[] = [];
    const moveService = MCTSMoveGenerationService.getInstance();
    const enemyVictoryRow = enemyColor === "white" ? 7 : 0;

    this.addLog(
      `🛡️ Checking defensive blocks - moves that preserve current blocking positions`
    );

    for (const moveInfo of allMoves) {
      // Simulate making this move
      const testGameState = this.simulateMove(gameState, moveInfo);

      // Check if enemy can still race after our move
      const originalTurn = testGameState.isLightTurn;
      testGameState.isLightTurn = enemyColor === "white";

      try {
        const enemyMoves = moveService.getAllMoves(testGameState);

        // Check if enemy can reach victory row after our move
        const canEnemyWin = enemyMoves.some(
          (enemyMove) => enemyMove.move.position.row === enemyVictoryRow
        );

        if (!canEnemyWin) {
          // This move preserves our defensive position!
          safeDefensiveMoves.push(moveInfo);
          this.addLog(
            `✅ Defensive block: [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] -> [${moveInfo.move.position.row},${moveInfo.move.position.col}]`
          );
        }
      } finally {
        testGameState.isLightTurn = originalTurn;
      }
    }

    if (safeDefensiveMoves.length < allMoves.length) {
      const dangerousCount = allMoves.length - safeDefensiveMoves.length;
      this.addLog(
        `⚠️ Found ${dangerousCount} moves that would open enemy race paths`
      );
    }

    return safeDefensiveMoves;
  }

  private simulateMove(gameState: any, moveInfo: MoveInfo): any {
    // Create a deep copy of the game state with proper piece prototypes
    const testState = cloneFullGameState(gameState);

    // Apply the move to the test state
    const fromPos = moveInfo.fromPosition;
    const toPos = moveInfo.move.position;

    // Get the piece to move
    const fromSquare = testState.board[fromPos.row][fromPos.col];
    if (fromSquare.Pieces.length === 0) return testState;

    // Create a new piece instance with proper prototype
    const piece = clonePieceWithPrototype(fromSquare.Pieces[0]);

    // Move the piece
    fromSquare.Pieces = [];
    testState.board[toPos.row][toPos.col].Pieces = [piece];

    return testState;
  }

  private moveCaptures(
    moveInfo: MoveInfo,
    gameState: any,
    targetType: string
  ): boolean {
    const targetSquare =
      gameState.board[moveInfo.move.position.row][moveInfo.move.position.col];
    if (targetSquare.Pieces && targetSquare.Pieces.length > 0) {
      const targetPiece = targetSquare.Pieces[0];
      return targetType === "king" ? targetPiece.isKing : false;
    }
    return false;
  }

  private doesMoveBlockKingThreats(
    moveInfo: MoveInfo,
    kingThreats: Position[],
    gameState: any
  ): boolean {
    const movePos = moveInfo.move.position;

    // Check if we're moving to any of the king's threat squares
    for (const threat of kingThreats) {
      if (movePos.row === threat.row && movePos.col === threat.col) {
        // Verify this is a real block (king can't capture our piece)
        if (this.isRealKingBlock(moveInfo, gameState)) {
          return true;
        }
      }
    }

    return false;
  }

  private isRealKingBlock(moveInfo: MoveInfo, gameState: any): boolean {
    // Get the piece we're moving
    const blockingPiece = this.getPieceAt(
      gameState.board,
      moveInfo.fromPosition
    );
    if (!blockingPiece) return false;

    const pieceType = blockingPiece.type.toLowerCase();

    // Trees cannot be captured by kings
    if (pieceType === "tree") {
      this.addLog(
        `✅ Tree block at [${moveInfo.move.position.row},${moveInfo.move.position.col}] - king cannot capture`
      );
      return true;
    }

    // CRITICAL FIX: Check if our blocking piece would be defended
    // If king captures a defended piece = kingslayer opportunity = king won't capture
    if (this.isBlockingPieceDefended(moveInfo, gameState)) {
      this.addLog(
        `✅ DEFENDED ${blockingPiece.type} block at [${moveInfo.move.position.row},${moveInfo.move.position.col}] - king capture = kingslayer!`
      );
      return true;
    }

    // Only if piece is undefended, king can safely capture
    this.addLog(
      `❌ ${blockingPiece.type} at [${moveInfo.move.position.row},${moveInfo.move.position.col}] - undefended, king can capture safely`
    );
    return false;
  }

  /**
   * CRITICAL NEW METHOD: Check if our blocking piece would be defended after moving
   * This fixes the bug where defended pieces were rejected as blocking moves
   */
  private isBlockingPieceDefended(moveInfo: MoveInfo, gameState: any): boolean {
    try {
      // Simulate placing our piece at the blocking position
      const testState = this.simulateMove(gameState, moveInfo);

      // Switch to enemy turn (king's turn to potentially capture)
      const originalTurn = testState.isLightTurn;
      testState.isLightTurn = !testState.isLightTurn;

      // Simulate enemy king capturing our blocking piece
      const kingCapture = this.simulateKingCapture(
        testState,
        moveInfo.move.position
      );
      if (!kingCapture) return false; // King can't even reach to capture

      // Switch back to our turn after king captures
      kingCapture.isLightTurn = !kingCapture.isLightTurn;

      // Check if we can capture the king after it captures our piece
      const moveService = MCTSMoveGenerationService.getInstance();
      const ourCounterMoves = moveService.getAllMoves(kingCapture);

      // Look for any move that captures the king at the blocking position
      for (const counterMove of ourCounterMoves) {
        if (
          counterMove.move.position.row === moveInfo.move.position.row &&
          counterMove.move.position.col === moveInfo.move.position.col
        ) {
          // We can recapture the king = kingslayer victory!
          return true;
        }
      }

      return false; // We cannot recapture the king
    } catch (error) {
      // If simulation fails, assume undefended (conservative)
      return false;
    }
  }

  /**
   * Simulate enemy king capturing our piece at the given position
   */
  private simulateKingCapture(
    gameState: any,
    targetPosition: Position
  ): any | null {
    try {
      // Find enemy king
      const enemyColor = gameState.isLightTurn ? "white" : "black";
      const kingPosition = this.findKing(gameState.board, enemyColor);

      if (!kingPosition) return null;

      // Check if king can move to capture position
      const moveService = MCTSMoveGenerationService.getInstance();
      const kingMoves = moveService
        .getAllMoves(gameState)
        .filter(
          (move) =>
            move.fromPosition.row === kingPosition.row &&
            move.fromPosition.col === kingPosition.col &&
            move.move.position.row === targetPosition.row &&
            move.move.position.col === targetPosition.col
        );

      if (kingMoves.length === 0) return null; // King can't reach

      // Simulate the king capture
      const captureState = cloneFullGameState(gameState);
      const kingPiece = this.getPieceAt(captureState.board, kingPosition);

      if (!kingPiece) return null;

      // Apply king capture move
      applyMoveWithSnakeRespawn(
        captureState,
        kingPosition,
        kingMoves[0].move,
        kingPiece
      );

      return captureState;
    } catch (error) {
      return null;
    }
  }

  private getPieceAt(board: any[][], position: Position): IPiece | null {
    if (
      position.row < 0 ||
      position.row >= board.length ||
      position.col < 0 ||
      position.col >= board[position.row].length
    ) {
      return null;
    }

    const square = board[position.row][position.col];
    if (!square || !square.Pieces || square.Pieces.length === 0) {
      return null;
    }

    return square.Pieces[0];
  }
}

